import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useUser } from "../../hooks/useAuth";
import { useExtraMaterials } from "../../hooks/useExtraMaterials";
import { useMyLessons, useScheduleNotifications } from "../../hooks/useLessons";
import { useAuthStore } from "../../store";
import type { LessonListItem } from "../../types/lesson";

const LEVELS = ["A1", "A2", "A2+", "B1", "B2", "C1", "C2"] as const;

// Program starts January 5, 2026
const PROGRAM_START = new Date(2026, 0, 5); // Jan 5, 2026

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Helper to format schedule for display
 */
function formatScheduleDisplay(schedule: any): string {
  if (!schedule) return "Not set";

  const dayMap: Record<string, string> = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
    Mon: "Mon",
    Tue: "Tue",
    Wed: "Wed",
    Thu: "Thu",
    Fri: "Fri",
    Sat: "Sat",
    Sun: "Sun",
  };

  // New format with scheduleItems array
  if (schedule.scheduleItems && Array.isArray(schedule.scheduleItems)) {
    if (schedule.scheduleItems.length === 0) return "Not set";

    // Check if all times are the same
    const times = schedule.scheduleItems.map((item: any) => item.time);
    const allSameTime = times.every((t: string) => t === times[0]);

    if (allSameTime && times[0]) {
      // Format: "Tue, Thu at 11:00"
      const days = schedule.scheduleItems
        .map((item: any) => {
          const dayRaw = (item.day || item.dayFull || "").toLowerCase();
          return dayMap[dayRaw] || dayRaw;
        })
        .filter(Boolean)
        .join(", ");
      return `${days} at ${times[0]}`;
    } else {
      // Format: "Tue 11:00, Thu 08:00"
      return schedule.scheduleItems
        .map((item: any) => {
          const dayRaw = (item.day || item.dayFull || "").toLowerCase();
          const day = dayMap[dayRaw] || dayRaw;
          const time = item.time || "";
          return time ? `${day} ${time}` : day;
        })
        .filter(Boolean)
        .join(", ");
    }
  }

  // Legacy format
  if (schedule.days && Array.isArray(schedule.days) && schedule.days.length > 0) {
    const time = schedule.time || "";
    if (time) {
      return `${schedule.days.join(", ")} at ${time}`;
    }
    return schedule.days.join(", ");
  }

  return "Not set";
}

/**
 * Helper to extract day abbreviations from schedule (for calendar)
 * Returns array like ["Tue", "Thu"]
 */
function extractScheduleDays(schedule: any): string[] {
  if (!schedule) return [];

  const dayMap: Record<string, string> = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
    Mon: "Mon",
    Tue: "Tue",
    Wed: "Wed",
    Thu: "Thu",
    Fri: "Fri",
    Sat: "Sat",
    Sun: "Sun",
  };

  // New format with scheduleItems array
  if (schedule.scheduleItems && Array.isArray(schedule.scheduleItems)) {
    return schedule.scheduleItems
      .map((item: any) => {
        const dayRaw = (item.day || item.dayFull || "").toLowerCase();
        return dayMap[dayRaw] || dayRaw;
      })
      .filter(Boolean);
  }

  // Legacy format
  if (schedule.days && Array.isArray(schedule.days)) {
    return schedule.days
      .map((day: string) => {
        const dayRaw = (day || "").toLowerCase();
        return dayMap[dayRaw] || dayMap[day] || day;
      })
      .filter(Boolean);
  }

  return [];
}

// Get calendar days for a month
function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const days: (number | null)[] = [];
  for (let i = 0; i < startingDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  return days;
}

/**
 * Current calendar month (1-12). Lessons unlock by absolute calendar month
 * (the month chosen per lesson), not relative to the student's intake date.
 * The classStartDate argument is kept for call-site compatibility but unused.
 */
function getCurrentProgramMonth(_classStartDate?: string | null): number {
  return new Date().getMonth() + 1;
}

/**
 * Check if a lesson is unlocked based on its monthAvailable and current program month.
 * Intro lessons (order 0) are always unlocked.
 */
function isLessonUnlocked(
  lesson: { monthAvailable: number; order: number },
  currentProgramMonth: number,
  isAdmin: boolean,
): boolean {
  // Admins and teachers can see all lessons
  if (isAdmin) return true;

  // Intro lessons are always available
  if (lesson.order === 0) return true;

  // Check if current month is >= lesson's required month
  return currentProgramMonth >= lesson.monthAvailable;
}

/**
 * Helper to get lesson display title (Introduction for order 0)
 */
function getLessonTitle(lesson: { order: number; topic: string }): string {
  if (lesson.order === 0) {
    return `Introduction: ${lesson.topic}`;
  }
  return `Lesson ${lesson.order}: ${lesson.topic}`;
}

/**
 * Helper to build view URL for the resource viewer page
 */
function buildViewUrl(params: {
  url: string;
  title: string;
  type: "files" | "homework";
  level?: string;
  lesson?: number;
}): string {
  const searchParams = new URLSearchParams({
    url: params.url,
    title: params.title,
    type: params.type,
  });
  if (params.level) searchParams.set("level", params.level);
  if (params.lesson) searchParams.set("lesson", String(params.lesson));
  return `/lessons/view?${searchParams.toString()}`;
}

export function LessonsPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const isAdmin = user?.role === "admin" || user?.role === "teacher";

  // Fetch fresh user data from backend (includes updated class links)
  const { data: backendUser, isLoading: isUserLoading } = useUser();

  // Sync backend user data with Zustand store when it changes
  useEffect(() => {
    if (backendUser) {
      setUser(backendUser);
    }
  }, [backendUser, setUser]);

  // State for level selector (admin/teacher only)
  const [selectedLevel, setSelectedLevel] = useState<string>("A1");

  // Toggle between list and calendar view
  const [viewMode, setViewMode] = useState<"list" | "calendar">("calendar");

  // For students, no level param is passed (backend uses their assigned level)
  // For admin/teacher, we pass the selected level
  const {
    data,
    isLoading: isLessonsLoading,
    error,
  } = useMyLessons(isAdmin ? selectedLevel : undefined);

  // Fetch schedule notifications
  const { data: notificationsData } = useScheduleNotifications();

  // Extra materials / homework links scoped to this student (all/level/class)
  const { data: extraMaterials } = useExtraMaterials();

  // Use backendUser (fresh from API) if available, otherwise fallback to Zustand user
  const currentUser = backendUser || user;

  // Get the student's class meet link (prioritize over level meet link)
  // Use currentUser to always get the freshest data
  const classMeetLink = currentUser?.classInfo?.meetLink;
  const classWhiteboardUrl = currentUser?.classInfo?.whiteboardUrl;

  // Combined loading - wait for both user data and lessons
  const isLoading = isUserLoading || isLessonsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-600 font-medium">Failed to load lessons</p>
          <p className="text-earth-700 text-sm mt-1">Please try again later</p>
        </div>
      </div>
    );
  }

  const { level, levelResources, lessons } = data;

  // Determine which meet link to use (class-specific > level-specific)
  // Convert null to undefined to satisfy TypeScript
  const meetLink = classMeetLink || levelResources?.meet?.url || undefined;

  // Determine which whiteboard URL to use (class-specific > level-specific)
  // Convert null to undefined to satisfy TypeScript
  const whiteboardUrl = classWhiteboardUrl || levelResources?.whiteboard?.url || undefined;

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-10">
        {/* Header */}
        <div className="mb-5 sm:mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 sm:mb-3 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-display font-bold text-earth-800">
                Level {level.code} - {level.name}
              </h1>
              <p className="text-earth-700 text-sm mt-0.5 sm:mt-1">
                {lessons.length} lessons • 6-month program
              </p>
            </div>

            {/* Level selector for admin/teacher */}
            {isAdmin && (
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-3 py-2 bg-white border border-earth-200 rounded-lg text-earth-700 text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 self-start"
              >
                {LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Module Resources - Fixed resources for the entire level */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5 mb-5 sm:mb-8">
          <h2 className="text-xs sm:text-sm font-medium text-earth-700 uppercase tracking-wide mb-3 sm:mb-4">
            Module Resources
          </h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
            {/* Google Meet */}
            {meetLink ? (
              <a
                href={meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 rounded-lg transition-colors group border border-emerald-100"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-500 rounded-lg flex items-center justify-center group-hover:bg-emerald-600 transition-colors shrink-0">
                  <i className="ri-video-chat-line text-white text-sm sm:text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-emerald-900 text-xs sm:text-sm">
                    Google Meet
                  </p>
                  <p className="text-[10px] sm:text-xs text-emerald-700 truncate">
                    {currentUser?.classInfo?.schedule
                      ? formatScheduleDisplay(currentUser.classInfo.schedule)
                      : "Join class"}
                  </p>
                </div>
                <i className="ri-external-link-line text-emerald-600"></i>
              </a>
            ) : (
              <div className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-earth-50/50 rounded-lg opacity-50">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-earth-100 rounded-lg flex items-center justify-center">
                  <i className="ri-video-chat-line text-earth-400 text-sm sm:text-base"></i>
                </div>
                <div>
                  <p className="font-medium text-earth-500 text-xs sm:text-sm">
                    Google Meet
                  </p>
                  <p className="text-[10px] sm:text-xs text-earth-400">
                    Not configured
                  </p>
                </div>
              </div>
            )}

            {/* Whiteboard - Opens externally (Canva doesn't allow iframe embedding) */}
            {whiteboardUrl ? (
              <a
                href={whiteboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 rounded-lg transition-colors group text-left border border-purple-100"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-purple-500 rounded-lg flex items-center justify-center group-hover:bg-purple-600 transition-colors shrink-0">
                  <i className="ri-pencil-ruler-2-line text-white text-sm sm:text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-purple-900 text-xs sm:text-sm">
                    Whiteboard
                  </p>
                  <p className="text-[10px] sm:text-xs text-purple-700 truncate">
                    Canva board
                  </p>
                </div>
                <i className="ri-external-link-line text-purple-600"></i>
              </a>
            ) : (
              <div className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-earth-50/50 rounded-lg opacity-50">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-earth-100 rounded-lg flex items-center justify-center">
                  <i className="ri-pencil-ruler-2-line text-earth-400 text-sm sm:text-base"></i>
                </div>
                <div>
                  <p className="font-medium text-earth-500 text-xs sm:text-sm">
                    Whiteboard
                  </p>
                  <p className="text-[10px] sm:text-xs text-earth-400">
                    Not configured
                  </p>
                </div>
              </div>
            )}

            {/* Homework */}
            {levelResources?.homework?.url ? (
              <button
                onClick={() =>
                  navigate(
                    buildViewUrl({
                      url: levelResources.homework!.url!,
                      title:
                        levelResources.homework!.title ||
                        `${level.code} Homework`,
                      type: "homework",
                      level: level.code,
                    }),
                  )
                }
                className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-orange-50 hover:bg-orange-100 active:bg-orange-200 rounded-lg transition-colors group text-left border border-orange-100"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-orange-500 rounded-lg flex items-center justify-center group-hover:bg-orange-600 transition-colors shrink-0">
                  <i className="ri-todo-line text-white text-sm sm:text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-orange-900 text-xs sm:text-sm">
                    Homework
                  </p>
                  <p className="text-[10px] sm:text-xs text-orange-700 truncate">
                    Study Planner
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-orange-600"></i>
              </button>
            ) : (
              <div className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-earth-50/50 rounded-lg opacity-50">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-earth-100 rounded-lg flex items-center justify-center">
                  <i className="ri-todo-line text-earth-400 text-sm sm:text-base"></i>
                </div>
                <div>
                  <p className="font-medium text-earth-500 text-xs sm:text-sm">
                    Homework
                  </p>
                  <p className="text-[10px] sm:text-xs text-earth-400">
                    Not configured
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extra Materials / Homework (links shared by the teacher) */}
        {extraMaterials && extraMaterials.length > 0 && (
          <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5 mb-5 sm:mb-8">
            <h2 className="text-xs sm:text-sm font-medium text-earth-700 uppercase tracking-wide mb-3 sm:mb-4">
              Extra Materials
            </h2>
            <div className="space-y-2">
              {extraMaterials.map((m) => (
                <a
                  key={m.id}
                  href={m.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-lg transition-colors group border border-blue-100"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-500 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors shrink-0">
                    <i className="ri-attachment-2 text-white text-sm sm:text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-blue-900 text-xs sm:text-sm truncate">
                      {m.title}
                    </p>
                    <p className="text-[10px] sm:text-xs text-blue-700 truncate capitalize">
                      {m.description || m.materialType}
                    </p>
                  </div>
                  <i className="ri-external-link-line text-blue-600"></i>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xs sm:text-sm font-medium text-earth-700 uppercase tracking-wide">
            {viewMode === "list" ? "Lessons" : "Calendar"}
          </h2>
          <div className="flex bg-earth-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("calendar")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === "calendar"
                  ? "bg-white text-earth-800 shadow-sm"
                  : "text-earth-700 hover:text-earth-700"
              }`}
            >
              <i className="ri-calendar-line"></i>
              <span>Cal</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-white text-earth-800 shadow-sm"
                  : "text-earth-700 hover:text-earth-700"
              }`}
            >
              <i className="ri-list-unordered"></i>
              <span>List</span>
            </button>
          </div>
        </div>

        {/* Calendar View */}
        {viewMode === "calendar" && (
          <ProgramCalendar
            lessons={lessons}
            scheduleDays={currentUser?.classInfo?.schedule ? extractScheduleDays(currentUser.classInfo.schedule) : undefined}
            notifications={notificationsData?.notifications || []}
            isAdmin={isAdmin}
            meetLink={meetLink}
            startDate={currentUser?.classInfo?.startDate}
            onLessonClick={(lessonOrder) => {
              setViewMode("list");
              // Small delay to allow view to switch, then scroll
              setTimeout(() => {
                const element = document.getElementById(
                  `lesson-${lessonOrder}`,
                );
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  element.classList.add("ring-2", "ring-olive-500");
                  setTimeout(
                    () => element.classList.remove("ring-2", "ring-olive-500"),
                    2000,
                  );
                }
              }, 100);
            }}
          />
        )}

        {/* Lessons List */}
        {viewMode === "list" && (
          <div className="space-y-3">
            {lessons.map((lesson) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                levelCode={level.code}
                navigate={navigate}
                isAdmin={isAdmin}
                startDate={currentUser?.classInfo?.startDate}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {lessons.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-open-line text-2xl text-earth-400"></i>
            </div>
            <p className="text-earth-600 font-medium">No lessons available</p>
            <p className="text-earth-700 text-sm">
              Lessons will appear here when available
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface LessonRowProps {
  lesson: LessonListItem;
  levelCode: string;
  navigate: ReturnType<typeof useNavigate>;
  isAdmin: boolean;
  startDate?: string | null;
}

function LessonRow({ lesson, levelCode, navigate, isAdmin, startDate }: LessonRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasFiles = !!lesson.filesUrl;
  const isIntro = lesson.order === 0;
  const displayNumber = isIntro ? "Intro" : String(lesson.order);

  // Check if lesson is unlocked
  const currentProgramMonth = getCurrentProgramMonth(startDate);
  const isUnlocked = isLessonUnlocked(lesson, currentProgramMonth, isAdmin);

  // If locked, show a simplified locked state
  if (!isUnlocked) {
    return (
      <div
        id={`lesson-${lesson.order}`}
        className="bg-earth-50/50 rounded-xl border border-earth-200 overflow-hidden opacity-60"
      >
        <div className="flex items-center gap-2.5 sm:gap-4 p-3 sm:p-4">
          {/* Lesson number with lock */}
          <div className="w-8 sm:w-10 h-8 sm:h-10 bg-earth-200 rounded-lg flex items-center justify-center flex-shrink-0 relative">
            <span className="text-xs sm:text-sm font-semibold text-earth-400">
              {displayNumber}
            </span>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-earth-300 rounded-full flex items-center justify-center">
              <i className="ri-lock-line text-[10px] text-earth-500"></i>
            </div>
          </div>

          {/* Lesson info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-earth-500 text-sm sm:text-base truncate">
              {lesson.topic}
            </h3>
            <p className="text-xs sm:text-sm text-earth-400 truncate">
              Disponível em {MONTH_NAMES[lesson.monthAvailable - 1]}
            </p>
          </div>

          {/* Lock icon */}
          <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-earth-200 rounded-lg text-xs sm:text-sm text-earth-500 shrink-0">
            <i className="ri-lock-line"></i>
            <span className="hidden xs:inline">Bloqueado</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`lesson-${lesson.order}`}
      className="bg-white rounded-xl border border-earth-200 overflow-hidden transition-all"
    >
      {/* Main row */}
      <div
        className="flex items-center gap-2.5 sm:gap-4 p-3 sm:p-4 cursor-pointer hover:bg-earth-50/50 active:bg-earth-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Lesson number */}
        <div
          className={`${isIntro ? "w-10 sm:w-12" : "w-8 sm:w-10"} h-8 sm:h-10 ${isIntro ? "bg-emerald-100" : "bg-earth-100"} rounded-lg flex items-center justify-center flex-shrink-0`}
        >
          <span
            className={`text-xs sm:text-sm font-semibold ${isIntro ? "text-emerald-600" : "text-earth-600"}`}
          >
            {displayNumber}
          </span>
        </div>

        {/* Lesson info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-earth-800 text-sm sm:text-base truncate">
            {lesson.topic}
          </h3>
          <p className="text-xs sm:text-sm text-earth-700 truncate">
            {MONTH_NAMES[lesson.monthAvailable - 1]}
            {lesson.grammarFocus && ` • ${lesson.grammarFocus}`}
          </p>
        </div>

        {/* Quick action - Files */}
        {hasFiles && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                buildViewUrl({
                  url: lesson.filesUrl!,
                  title: getLessonTitle(lesson),
                  type: "files",
                  level: levelCode,
                  lesson: lesson.order,
                }),
              );
            }}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-earth-100 hover:bg-earth-200 active:bg-earth-300 rounded-lg text-xs sm:text-sm text-earth-700 transition-colors shrink-0"
          >
            <i className="ri-file-text-line"></i>
            <span className="hidden xs:inline">Files</span>
          </button>
        )}

        {/* Expand icon */}
        <i
          className={`ri-arrow-down-s-line text-earth-400 transition-transform shrink-0 ${
            isExpanded ? "rotate-180" : ""
          }`}
        ></i>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0 border-t border-earth-100">
          <div className="pt-3 sm:pt-4">
            {/* Description */}
            {lesson.description && (
              <p className="text-xs sm:text-sm text-earth-600 mb-3 sm:mb-4">
                {lesson.description}
              </p>
            )}

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              {lesson.grammarFocus && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-earth-100 text-earth-600 rounded text-[10px] sm:text-xs">
                  <i className="ri-book-2-line"></i>
                  {lesson.grammarFocus}
                </span>
              )}
              {lesson.vocabularyFocus && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-earth-100 text-earth-600 rounded text-[10px] sm:text-xs">
                  <i className="ri-chat-quote-line"></i>
                  {lesson.vocabularyFocus}
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {hasFiles && (
                <button
                  onClick={() =>
                    navigate(
                      buildViewUrl({
                        url: lesson.filesUrl!,
                        title: getLessonTitle(lesson),
                        type: "files",
                        level: levelCode,
                        lesson: lesson.order,
                      }),
                    )
                  }
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-earth-600 hover:bg-earth-700 active:bg-earth-800 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors"
                >
                  <i className="ri-file-text-line"></i>
                  Open Files
                </button>
              )}

              {lesson.filesUrl && (
                <a
                  href={lesson.filesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 text-earth-700 hover:text-earth-700 text-xs sm:text-sm transition-colors"
                >
                  <i className="ri-external-link-line"></i>
                  <span className="hidden xs:inline">Open externally</span>
                  <span className="xs:hidden">External</span>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Format date for display in toasts
 */
function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Map day abbreviations to day of week numbers (0=Sun, 1=Mon, ...)
 */
const DAY_NAME_TO_NUMBER: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Calendar component showing the 6-month program schedule
 */
interface ProgramCalendarProps {
  lessons: LessonListItem[];
  scheduleDays?: string[]; // e.g., ["Tue", "Thu"] or ["Mon", "Wed"]
  notifications?: any[]; // Schedule change notifications
  isAdmin: boolean;
  meetLink?: string; // Google Meet link
  onLessonClick?: (lessonOrder: number) => void;
  startDate?: string | null; // Class group start date for calculating program month
}

function ProgramCalendar({
  lessons,
  scheduleDays,
  notifications = [],
  isAdmin,
  meetLink,
  onLessonClick,
  startDate,
}: ProgramCalendarProps) {
  // Calculate current program month for lock status
  const currentProgramMonth = useMemo(() => getCurrentProgramMonth(startDate), [startDate]);

  // Calculate initial month to display (current calendar month, 0-11)
  const initialMonth = useMemo(() => {
    const month = currentProgramMonth - 1; // Convert 1-based to 0-based index
    return Math.max(0, Math.min(11, month)); // Clamp between 0-11
  }, [currentProgramMonth]);

  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  // Create a map of dates with schedule changes
  const scheduleChangesMap = useMemo(() => {
    const map: Record<string, { type: 'cancelled' | 'rescheduled', notification: any }> = {};

    notifications.forEach((notification) => {
      // Mark original date as cancelled/rescheduled
      const originalDate = notification.originalDate;
      map[originalDate] = {
        type: notification.changeType as 'cancelled' | 'rescheduled',
        notification,
      };

      // If rescheduled, mark new date
      if (notification.changeType === 'rescheduled' && notification.newDate) {
        map[notification.newDate] = {
          type: 'rescheduled',
          notification: { ...notification, isNewDate: true },
        };
      }
    });

    return map;
  }, [notifications]);

  // Convert schedule day names to day numbers
  // Default to Tue/Thu if no schedule provided
  const classDayNumbers = useMemo(() => {
    if (scheduleDays && scheduleDays.length > 0) {
      return scheduleDays
        .map((d) => DAY_NAME_TO_NUMBER[d])
        .filter((n) => n !== undefined);
    }
    return [2, 4]; // Default: Tuesday and Thursday
  }, [scheduleDays]);

  // Generate the 12 calendar months of the program year.
  const programMonths = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(PROGRAM_START.getFullYear(), i, 1);
      return {
        index: i + 1, // Calendar month 1-12
        year: date.getFullYear(),
        month: date.getMonth(),
        name: MONTH_NAMES[date.getMonth()],
        fullName: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
      };
    });
  }, []);

  // Get lessons for selected month
  const monthLessons = useMemo(() => {
    return lessons.filter((l) => l.monthAvailable === selectedMonth + 1);
  }, [lessons, selectedMonth]);

  // Filter notifications relevant to the selected month
  const relevantNotifications = useMemo(() => {
    if (!notifications || notifications.length === 0) return [];

    const monthInfo = programMonths[selectedMonth];
    const monthStart = new Date(monthInfo.year, monthInfo.month, 1);
    const monthEnd = new Date(monthInfo.year, monthInfo.month + 1, 0);

    return notifications.filter((notification) => {
      // Check if originalDate is in the selected month
      const originalDate = new Date(notification.originalDate);
      const isOriginalInMonth =
        originalDate >= monthStart && originalDate <= monthEnd;

      // Check if newDate is in the selected month (for rescheduled classes)
      if (notification.newDate) {
        const newDate = new Date(notification.newDate);
        const isNewInMonth = newDate >= monthStart && newDate <= monthEnd;
        return isOriginalInMonth || isNewInMonth;
      }

      // For cancelled classes, only show if original date is in the selected month
      return isOriginalInMonth;
    });
  }, [notifications, selectedMonth, programMonths]);

  // Get today's date info
  const today = useMemo(() => {
    const now = new Date();
    return {
      day: now.getDate(),
      month: now.getMonth(),
      year: now.getFullYear(),
    };
  }, []);

  // Current selected month info
  const currentMonthInfo = programMonths[selectedMonth];
  const calendarDays = useMemo(
    () => getCalendarDays(currentMonthInfo.year, currentMonthInfo.month),
    [currentMonthInfo.year, currentMonthInfo.month],
  );

  // Check if a day is today
  const isToday = (day: number | null) => {
    if (!day) return false;
    return (
      day === today.day &&
      currentMonthInfo.month === today.month &&
      currentMonthInfo.year === today.year
    );
  };

  // Check if a day is before program start (Jan 5, 2026)
  const isBeforeStart = (day: number | null) => {
    if (!day) return false;
    const date = new Date(currentMonthInfo.year, currentMonthInfo.month, day);
    return date < PROGRAM_START;
  };

  // Check if a day is a class day based on the student's schedule
  const isClassDay = (day: number | null) => {
    if (!day) return false;
    const date = new Date(currentMonthInfo.year, currentMonthInfo.month, day);
    if (date < PROGRAM_START) return false;
    const dayOfWeek = date.getDay();
    return classDayNumbers.includes(dayOfWeek);
  };

  // Count class days in the current month (for mapping to lessons)
  const getClassDayIndex = (day: number | null) => {
    if (!day || !isClassDay(day)) return null;

    let count = 0;
    for (let d = 1; d <= day; d++) {
      if (isClassDay(d)) count++;
    }
    return count;
  };

  // Get lesson for a class day based on which class it is in the month
  const getLessonForClassDay = (day: number | null) => {
    const classIndex = getClassDayIndex(day);
    if (!classIndex || monthLessons.length === 0) return null;

    const classesPerLesson = 2;
    const lessonIdx = Math.min(
      Math.ceil(classIndex / classesPerLesson) - 1,
      monthLessons.length - 1,
    );
    return monthLessons[lessonIdx] || null;
  };

  // Handle day click
  const handleDayClick = (day: number | null) => {
    if (!day) return;

    const dateStr = `${currentMonthInfo.year}-${String(currentMonthInfo.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const scheduleChange = scheduleChangesMap[dateStr];

    // Check if it's a class day
    const isClassDayCheck = isClassDay(day);

    // If there's a schedule change
    if (scheduleChange) {
      const { type, notification } = scheduleChange;

      // If it's the original date of a cancelled class
      if (type === 'cancelled' && !notification.isNewDate) {
        toast.error(
          <div>
            <p className="font-semibold">Class Cancelled</p>
            <p className="text-sm mt-1">
              {notification.reason || "This class has been cancelled."}
            </p>
          </div>,
          { duration: 5000 }
        );
        return;
      }

      // If it's the original date of a rescheduled class
      if (type === 'rescheduled' && !notification.isNewDate) {
        const newDateFormatted = formatDateDisplay(notification.newDate);
        toast(
          <div>
            <p className="font-semibold">Class Rescheduled</p>
            <p className="text-sm mt-1">
              Moved to {newDateFormatted}
            </p>
            {notification.reason && (
              <p className="text-xs mt-1 opacity-75">{notification.reason}</p>
            )}
          </div>,
          {
            icon: (
              <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                <i className="ri-calendar-schedule-line text-white text-xs"></i>
              </div>
            ),
            duration: 5000
          }
        );
        return;
      }

      // If it's the new date of a rescheduled class, open meet link
      if (type === 'rescheduled' && notification.isNewDate) {
        if (meetLink) {
          window.open(meetLink, "_blank");
        } else {
          toast.error("No Google Meet link available");
        }
        return;
      }
    }

    // If it's a normal class day (no schedule changes)
    if (isClassDayCheck && !scheduleChange) {
      if (meetLink) {
        window.open(meetLink, "_blank");
      } else {
        toast.error("No Google Meet link available");
      }
      return;
    }

    // If it's not a class day and no schedule change, do nothing
  };

  return (
    <div className="bg-white rounded-xl border border-earth-200 p-4 mb-6">
      {/* Month Selector Pills */}
      <div className="flex gap-1 mb-4 overflow-x-auto">
        {programMonths.map((m, i) => (
          <button
            key={i}
            onClick={() => setSelectedMonth(i)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
              selectedMonth === i
                ? "bg-earth-600 text-white"
                : "bg-earth-100 text-earth-600 hover:bg-earth-200"
            }`}
          >
            {m.name}
          </button>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar - Left Side */}
        <div className="w-full lg:w-80 lg:flex-shrink-0">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-earth-800">
              {currentMonthInfo.fullName}
            </h3>
          </div>

          {/* Calendar Grid */}
          <div className="mb-3">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((day, i) => (
                <div
                  key={i}
                  className="text-center text-xs font-medium text-earth-700 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={index} className="aspect-square" />;
                }

                const dateStr = `${currentMonthInfo.year}-${String(currentMonthInfo.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const dayIsToday = isToday(day);
                const dayIsClassDay = isClassDay(day);
                const dayIsBeforeStart = isBeforeStart(day);
                const scheduleChange = scheduleChangesMap[dateStr];
                const lessonForDay = getLessonForClassDay(day);

                // Determine if this day is clickable
                const isClickable = dayIsClassDay || scheduleChange;

                return (
                  <button
                    key={index}
                    onClick={() => handleDayClick(day)}
                    disabled={!isClickable}
                    className={`aspect-square text-xs p-1 rounded transition-all relative ${
                      dayIsToday
                        ? "bg-earth-600 text-white font-bold"
                        : dayIsBeforeStart
                          ? "text-earth-300"
                          : dayIsClassDay && !scheduleChange
                            ? "bg-olive-100 text-olive-800 hover:bg-olive-200"
                            : "text-earth-600"
                    } ${
                      scheduleChange?.type === "cancelled" && !scheduleChange.notification.isNewDate
                        ? "bg-red-100 text-red-700 line-through hover:bg-red-200"
                        : scheduleChange?.type === "rescheduled" && !scheduleChange.notification.isNewDate
                          ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                          : scheduleChange?.notification.isNewDate
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : ""
                    } ${
                      isClickable ? "cursor-pointer" : "cursor-default"
                    }`}
                  >
                    {day}
                    {lessonForDay && !scheduleChange && (
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-olive-600 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-earth-700 pt-3 border-t border-earth-100">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-earth-600 rounded"></span>
              <span>Today</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-olive-100 rounded"></span>
              <span>Class</span>
            </div>
            {relevantNotifications && relevantNotifications.length > 0 && (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-red-100 rounded"></span>
                  <span>Cancelled</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-amber-100 rounded"></span>
                  <span>Moved from</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-green-100 rounded"></span>
                  <span>Moved to</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Lessons Info - Right Side */}
        <div className="flex-1 lg:border-l border-earth-100 pl-0 lg:pl-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-earth-700 uppercase tracking-wide">
              Month {currentMonthInfo.index} Lessons
            </h4>
            <span className="text-xs text-earth-700">
              {monthLessons.length} total
            </span>
          </div>

          {monthLessons.length > 0 ? (
            <div className="space-y-2">
              {monthLessons.map((lesson) => {
                const lessonUnlocked = isLessonUnlocked(
                  lesson,
                  currentProgramMonth,
                  isAdmin,
                );

                if (!lessonUnlocked) {
                  // Locked lesson display
                  return (
                    <div
                      key={lesson.id}
                      className="w-full text-left p-3 bg-earth-100/50 rounded-lg opacity-60"
                    >
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-earth-200 rounded text-xs flex items-center justify-center font-bold text-earth-400 flex-shrink-0 mt-0.5 relative">
                          {lesson.order}
                          <i className="ri-lock-line text-[8px] absolute -bottom-0.5 -right-0.5"></i>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-earth-500 truncate">
                            {lesson.topic}
                          </p>
                          <p className="text-xs text-earth-400 truncate mt-1">
                            Disponível em {MONTH_NAMES[lesson.monthAvailable - 1]}
                          </p>
                        </div>
                        <i className="ri-lock-line text-earth-400"></i>
                      </div>
                    </div>
                  );
                }

                // Unlocked lesson display
                return (
                  <button
                    key={lesson.id}
                    onClick={() => onLessonClick?.(lesson.order)}
                    className="w-full text-left p-3 bg-earth-50 hover:bg-olive-50 rounded-lg transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`${lesson.order === 0 ? "w-10" : "w-6"} h-6 ${lesson.order === 0 ? "bg-emerald-200 group-hover:bg-emerald-300" : "bg-earth-200 group-hover:bg-olive-200"} rounded text-xs flex items-center justify-center font-bold ${lesson.order === 0 ? "text-emerald-700" : "text-earth-600 group-hover:text-olive-700"} flex-shrink-0 mt-0.5`}
                      >
                        {lesson.order === 0 ? "Intro" : lesson.order}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-earth-800 group-hover:text-olive-800 truncate">
                          {lesson.topic}
                        </p>
                        {lesson.grammarFocus && (
                          <p className="text-xs text-earth-700 truncate mt-1">
                            {lesson.grammarFocus}
                          </p>
                        )}
                      </div>
                      <i className="ri-arrow-right-s-line text-earth-400 group-hover:text-olive-600"></i>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <i className="ri-calendar-close-line text-3xl text-earth-300"></i>
              <p className="text-sm text-earth-700 mt-2">
                No lessons this month
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Changes Section */}
      {relevantNotifications && relevantNotifications.length > 0 && (
        <div className="mt-6 pt-6 border-t border-earth-200">
          <h4 className="text-sm font-medium text-earth-700 mb-3 flex items-center gap-2">
            <i className="ri-calendar-event-line"></i>
            Schedule Changes
          </h4>
          <div className="space-y-2">
            {relevantNotifications.map((notification, idx) => {
              const isCancelled = notification.changeType === 'cancelled';
              const originalDate = new Date(notification.originalDate);
              const newDate = notification.newDate ? new Date(notification.newDate) : null;

              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    isCancelled
                      ? 'bg-red-50 border-red-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isCancelled ? 'bg-red-500' : 'bg-amber-500'
                    }`}>
                      <i className={`${
                        isCancelled ? 'ri-close-circle-line' : 'ri-calendar-schedule-line'
                      } text-white`}></i>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        isCancelled ? 'text-red-800' : 'text-amber-800'
                      }`}>
                        {isCancelled ? 'Class Cancelled' : 'Class Rescheduled'}
                      </p>

                      <div className="mt-2 space-y-1 text-xs">
                        <p className={isCancelled ? 'text-red-700' : 'text-amber-700'}>
                          <span className="font-medium">Original:</span>{' '}
                          {originalDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {notification.originalTime && ` at ${notification.originalTime}`}
                        </p>

                        {!isCancelled && newDate && (
                          <p className="text-green-700">
                            <span className="font-medium">New date:</span>{' '}
                            {newDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {notification.newTime && ` at ${notification.newTime}`}
                          </p>
                        )}

                        {notification.reason && (
                          <p className={`${isCancelled ? 'text-red-600' : 'text-amber-600'} mt-2 italic`}>
                            "{notification.reason}"
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
